import { useAuth } from "@/contexts/AuthContext";
import { useState, useEffect } from "react";
import { CreditCard, Calendar, Package, AlertCircle } from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import {
    fetchSubscription,
    type SubscriptionData
} from "@/services/billingService";

export default function BillingPage() {
    const { tenantId, user } = useAuth();
    const [subscription, setSubscription] = useState<SubscriptionData | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const loadSubscription = async () => {
            if (!tenantId) return;

            try {
                setIsLoading(true);
                setError(null);
                const data = await fetchSubscription(tenantId);
                setSubscription(data);
            } catch (err: any) {
                console.error("Error loading subscription:", err);
                setError("Failed to load subscription data");
            } finally {
                setIsLoading(false);
            }
        };

        loadSubscription();
    }, [tenantId]);


    const getStatusColor = (status: string) => {
        const normalizedStatus = status.toLowerCase();
        switch (normalizedStatus) {
            case 'active':
            case 'subscribed':
                return 'default';
            case 'pendingfulfillmentstart':
            case 'pending':
                return 'secondary';
            case 'suspended':
                return 'destructive';
            case 'unsubscribed':
                return 'outline';
            default:
                return 'secondary';
        }
    };

    const formatDate = (dateString: string | null) => {
        if (!dateString) return 'N/A';
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    const getPlanName = (payload: any): string => {
        return payload?.subscription?.name || payload?.planId || 'N/A';
    };

    const getOfferId = (payload: any): string => {
        return payload?.subscription?.offerId || payload?.offerId || 'N/A';
    };

    const formatBillingCycle = (termUnit: string | null): string => {
        if (!termUnit) return 'N/A';
        switch (termUnit.toUpperCase()) {
            case 'P1M':
                return 'Monthly';
            case 'P1Y':
                return 'Yearly';
            default:
                return termUnit;
        }
    };

    if (isLoading) {
        return (
            <>
                <div>
                    <h1 className="text-3xl font-bold tracking-tight mb-2">Billing & Subscription</h1>
                    <p className="text-muted-foreground mb-6">
                        Loading your subscription details...
                    </p>
                </div>
                <div className="rounded-lg border bg-card p-6 shadow-sm animate-pulse">
                    <div className="h-4 bg-muted rounded w-3/4 mb-3"></div>
                    <div className="h-8 bg-muted rounded w-1/2 mb-2"></div>
                    <div className="h-3 bg-muted rounded w-2/3"></div>
                </div>
            </>
        );
    }

    if (error) {
        return (
            <>
                <div>
                    <h1 className="text-3xl font-bold tracking-tight mb-2">Billing & Subscription</h1>
                    <p className="text-muted-foreground mb-6">
                        Manage your Microsoft Marketplace subscription
                    </p>
                </div>
                <Card className="border-destructive">
                    <CardHeader>
                        <CardTitle className="flex items-center gap-2 text-destructive">
                            <AlertCircle className="h-5 w-5" />
                            Error Loading Subscription
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <p className="text-muted-foreground">{error}</p>
                    </CardContent>
                </Card>
            </>
        );
    }

    if (!subscription) {
        return (
            <>
                <div>
                    <h1 className="text-3xl font-bold tracking-tight mb-2">Billing & Subscription</h1>
                    <p className="text-muted-foreground mb-6">
                        Manage your Microsoft Marketplace subscription
                    </p>
                </div>
                <Card>
                    <CardHeader>
                        <CardTitle>No Active Subscription</CardTitle>
                        <CardDescription>
                            You don't have an active Microsoft Marketplace subscription yet.
                        </CardDescription>
                    </CardHeader>
                    <CardContent>
                        <p className="text-muted-foreground mb-4">
                            To use AutoReply365, you need to subscribe through the Microsoft Azure Marketplace.
                        </p>
                        <Button asChild>
                            <a
                                href="https://azuremarketplace.microsoft.com"
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                Visit Azure Marketplace
                            </a>
                        </Button>
                    </CardContent>
                </Card>
            </>
        );
    }

    return (
        <>
            <div>
                <h1 className="text-3xl font-bold tracking-tight mb-2">Billing & Subscription</h1>
                <p className="text-muted-foreground mb-6">
                    Manage your Microsoft Marketplace subscription
                </p>
            </div>

            <div className="space-y-6">
                {/* Main Subscription Card */}
                <Card>
                    <CardHeader>
                        <div className="flex items-start justify-between">
                            <div>
                                <CardTitle className="flex items-center gap-2">
                                    <CreditCard className="h-5 w-5" />
                                    Current Subscription
                                </CardTitle>
                                <CardDescription className="mt-2">
                                    Subscription ID: {subscription.external_id}
                                </CardDescription>
                            </div>
                            <Badge variant={getStatusColor(subscription.status)}>
                                {subscription.status}
                            </Badge>
                        </div>
                    </CardHeader>
                    <CardContent className="space-y-6">
                        {/* Mailbox Quota */}
                        <div className="rounded-lg border-2 border-primary/20 bg-primary/5 p-4">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm font-medium text-muted-foreground">Licensed Mailboxes</p>
                                    <p className="text-3xl font-bold mt-1">{subscription.quantity}</p>
                                    <p className="text-xs text-muted-foreground mt-1">
                                        Maximum number of mailboxes you can configure with auto-reply rules
                                    </p>
                                </div>
                                <Package className="h-10 w-10 text-primary opacity-50" />
                            </div>
                        </div>

                        <Separator />

                        {/* Plan Information */}
                        <div>
                            <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                                <CreditCard className="h-4 w-4" />
                                Plan Details
                            </h3>
                            <div className="grid gap-3 md:grid-cols-2">
                                <div>
                                    <p className="text-sm text-muted-foreground">Plan Name</p>
                                    <p className="font-medium">{getPlanName(subscription.payload)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Offer ID</p>
                                    <p className="font-medium">{getOfferId(subscription.payload)}</p>
                                </div>
                            </div>
                            <div className="grid gap-3 md:grid-cols-2 mt-4">
                                <div>
                                    <p className="text-sm text-muted-foreground">Created</p>
                                    <p className="font-medium">{formatDate(subscription.created_at)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Last Updated</p>
                                    <p className="font-medium">{formatDate(subscription.updated_at)}</p>
                                </div>
                            </div>
                        </div>

                        <Separator />

                        {/* Billing Period */}
                        <div>
                            <h3 className="text-sm font-medium mb-3 flex items-center gap-2">
                                <Calendar className="h-4 w-4" />
                                Billing Information
                            </h3>
                            <div className="grid gap-3 md:grid-cols-3">
                                <div>
                                    <p className="text-sm text-muted-foreground">Billing Cycle</p>
                                    <p className="font-medium">{formatBillingCycle(subscription.term_unit)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Current Term Start</p>
                                    <p className="font-medium">{formatDate(subscription.term_start_date)}</p>
                                </div>
                                <div>
                                    <p className="text-sm text-muted-foreground">Current Term End</p>
                                    <p className="font-medium">{formatDate(subscription.term_end_date)}</p>
                                </div>
                            </div>
                        </div>

                        <Separator />

                        {/* Management Actions */}
                        <div>
                            <h3 className="text-sm font-medium mb-3">Manage Subscription</h3>
                            <div className="space-y-3">
                                <p className="text-sm text-muted-foreground">
                                    To cancel your subscription, change your plan, or update the number of licensed mailboxes, please visit the Azure Portal.
                                </p>
                                <div className="flex flex-wrap gap-3">
                                    <Button
                                        variant="default"
                                        asChild
                                    >
                                        <a
                                            href="https://portal.azure.com/#blade/HubsExtension/BrowseResourceBlade/resourceType/Microsoft.SaaS%2Fresources"
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            Configure in Azure Portal
                                        </a>
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </CardContent>
                </Card>

            </div>
        </>
    );
}
